import { EVENT_NAME } from '@cash-rewards/mparticlejs';
import {
  OptimizelyDecision,
  OptimizelyFlagProvider
} from '@cash-rewards/optimizely';
import { TrackPageScroll } from '@cash-rewards/reactjs';
import { GetServerSideProps, GetServerSidePropsContext } from 'next';
import { SWRConfig } from 'swr';

import createAxios, {
  handleAxiosResponseGracefuly,
  silentErrorHandler
} from '@/lib/axios';
import { getOffersOptimizelyDecisions } from '@/lib/optimizely';

import Layout from '@/components/layout/Layout';

import {
  CAROUSELS_API_PATH,
  FEATURED_ARTICLE_API_PATH,
  SECONDARY_BANNERS_API_PATH
} from '@/constants/api';
import { OffersServerResponse } from '@/modules/core/models/offersServerResponse';
import AppConfigProvider, {
  AppConfig,
  getAppConfig
} from '@/modules/core/providers/appConfigProvider';
import Shop, { ShopProps } from '@/modules/shop';
import ShopSeo from '@/modules/shop/components/ShopSeo';
import { Articles } from '@/modules/shop/models/article';
import {
  CarouselsResponse,
  Offer,
  OfferBanner,
  PersonalisedOfferResponseModel,
  PersonalisedStoreResponseModel,
  SecondaryBannerResponse,
  SecondaryBanners,
  Store,
  StoreModel
} from '@/modules/shop/models/offerModels';

/**
 * SVGR Support
 * Caveat: No React Props Type.
 *
 * You can override the next-env if the type is important to you
 * @see https://stackoverflow.com/questions/68103844/how-to-override-next-js-svg-module-declaration
 */

/* 
!STARTERCONF -> Select !STARTERCONF and CMD + SHIFT + F Before you begin editing, follow all comments with `STARTERCONF`,
 to customize the default configuration.
*/
interface ShopPageProps extends ShopProps {
  config: AppConfig;
  decisions: OptimizelyDecision[];
}

function ShopPage({
  config,
  featuredOffersResponse,
  boostedCashbackOffers,
  popularStores,
  personalisedStoresResponse,
  giftCardStores,
  trendingStores,
  secondaryBanners: secondayBanners,
  offerBanners,
  featuredArticles,
  decisions
}: Readonly<ShopPageProps>) {
  return (
    <SWRConfig value={{ revalidateOnFocus: false }}>
      <AppConfigProvider.Provider value={config}>
        <OptimizelyFlagProvider decisions={decisions}>
          <Layout>
            <ShopSeo />
            <TrackPageScroll eventName={EVENT_NAME.scroll_shophome}>
              <Shop
                featuredOffersResponse={featuredOffersResponse}
                boostedCashbackOffers={boostedCashbackOffers}
                popularStores={popularStores}
                personalisedStoresResponse={personalisedStoresResponse}
                giftCardStores={giftCardStores}
                trendingStores={trendingStores}
                secondaryBanners={secondayBanners}
                featuredArticles={featuredArticles}
                offerBanners={offerBanners}
              />
            </TrackPageScroll>
          </Layout>
        </OptimizelyFlagProvider>
      </AppConfigProvider.Provider>
    </SWRConfig>
  );
}

export const getServerSideProps: GetServerSideProps<ShopPageProps> = async (
  ctx: GetServerSidePropsContext
) => {
  const config = getAppConfig(ctx);

  const decisionsPromise = getOffersOptimizelyDecisions(ctx);
  const fetcher = createAxios({
    headers: {
      cookie: ctx.req.headers.cookie,
      'x-forwarded-for': ctx.req.headers['x-forwarded-for']
    }
  });
  const carouselsURL = `${config.API_URL}${CAROUSELS_API_PATH}`;
  const secondaryBannersURL = `${config.API_URL}${SECONDARY_BANNERS_API_PATH}`;
  const featuredArticlesURL = `${config.STRAPI_URL}${FEATURED_ARTICLE_API_PATH}`;
  const [
    carouselsResponse,
    secondaryBannersReponse,
    featuredArticlesResponse,
    decisions
  ]: [
    CarouselsResponse | null,
    SecondaryBannerResponse<SecondaryBanners> | null,
    Articles | null,
    OptimizelyDecision[]
  ] = await Promise.all([
    fetcher(carouselsURL, {
      method: 'POST',
      data: {
        carouselQueries: [
          {
            $type: 'offer',
            isBannerFeatured: true,
            includeAds: true,
            pageNumber: 0,
            pageSize: 0
          },
          {
            $type: 'offer',
            isFeatured: true,
            sort: 'Personalised',
            includeAds: true,
            pageNumber: 0,
            pageSize: 0
          },
          {
            $type: 'merchant',
            IsPersonalised: true,
            pageNumber: 1,
            pageSize: 100
          },
          {
            $type: 'offer',
            isCashbackIncreased: true,
            pageNumber: 0,
            pageSize: 0
          },
          {
            $type: 'merchant',
            categoryHyphenatedString: 'gift-cards',
            sort: 'Personalised',
            pageNumber: 1,
            pageSize: 50
          },
          {
            $type: 'merchant',
            IsPopular: true,
            includeAds: true,
            pageNumber: 1,
            pageSize: 50
          },
          {
            $type: 'merchant',
            isTrending: true,
            pageNumber: 1,
            pageSize: 50
          }
        ]
      }
    }).catch(silentErrorHandler),
    fetcher(secondaryBannersURL, {
      method: 'POST',
      data: { Placements: [{ placementId: 'shop-secondary' }] }
    }).catch(silentErrorHandler),
    fetcher(featuredArticlesURL).catch(silentErrorHandler),
    decisionsPromise
  ]).then(
    ([
      carouselsResult,
      secondaryBannersResult,
      articlesResult,
      decisionsResult
    ]: OffersServerResponse) => {
      return [
        handleAxiosResponseGracefuly<CarouselsResponse>(carouselsResult),
        handleAxiosResponseGracefuly<SecondaryBannerResponse<SecondaryBanners>>(
          secondaryBannersResult
        ),
        handleAxiosResponseGracefuly<Articles>(articlesResult),
        decisionsResult
      ];
    }
  );

  return {
    props: {
      config,
      offerBanners:
        (carouselsResponse?.carousels[0].data as OfferBanner[]) ?? null,
      featuredOffersResponse:
        (carouselsResponse?.carousels[1] as PersonalisedOfferResponseModel) ??
        null,
      personalisedStoresResponse:
        (carouselsResponse?.carousels[2] as PersonalisedStoreResponseModel) ??
        null,
      boostedCashbackOffers:
        (carouselsResponse?.carousels[3].data as Offer[]) ?? null,
      giftCardStores:
        (carouselsResponse?.carousels[4].data as StoreModel[]) ?? null,
      popularStores: (carouselsResponse?.carousels[5].data as Store[]) ?? null,
      trendingStores:
        (carouselsResponse?.carousels[6].data as StoreModel[]) ?? null,
      secondaryBanners:
        secondaryBannersReponse?.data?.['shop-secondary'] ?? null,
      featuredArticles: featuredArticlesResponse?.data ?? null,
      decisions
    }
  };
};

export default ShopPage;
